import { Add } from '@carbon/icons-react';
import { Button, ComboBox, RadioButton, RadioButtonGroup } from '@carbon/react';
import { asProblem, FormModal, hasPermission, problemMessage, useAdmin, useReferenceOptions, type ReferenceOption } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { TransientNotice } from '../common/Notices';

/**
 * The channel stores list's toolbar (FR-ONL-07): every store of a brand (or of the tenant) on an online channel at once.
 * Closed by default, so nothing takes orders until someone opens it; stores already on the channel keep their settings.
 */
export function AddChannelStoresButton({ refresh }: { refresh(): void }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const channels = useReferenceOptions('config.sales_channel');
  const brands = useReferenceOptions('org.brand');
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<string>();
  const [brand, setBrand] = useState<string>();
  const [status, setStatus] = useState<'closed' | 'open'>('closed');
  const [done, setDone] = useState<string>();

  const add = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/onlineordering/channel-stores/add-stores', {
        body: { salesChannelId: channel ?? '', brandId: brand ?? null, status },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (result) => {
      setOpen(false);
      setDone(t('channelStores.addAll.done', { added: Number(result.added), alreadyOn: Number(result.alreadyOn) }));
      refresh();
    },
  });

  if (!hasPermission(session, 'ho.online.channel_stores.manage')) return null;
  const label = (option: ReferenceOption | null | undefined) => option?.label ?? '';
  const channelOptions = channels.data ?? [];
  const brandOptions = brands.data ?? [];

  return (
    <>
      <Button kind="ghost" renderIcon={Add} onClick={() => setOpen(true)}>
        {t('channelStores.addAll.button')}
      </Button>
      {done && <TransientNotice kind="success" title={done} onDone={() => setDone(undefined)} />}
      {open && (
        <FormModal
          size="sm"
          title={t('channelStores.addAll.title')}
          primaryText={t('channelStores.addAll.confirm')}
          disabled={!channel}
          pending={add.isPending}
          error={add.error ? problemMessage(t, asProblem(add.error)) : null}
          onSubmit={() => add.mutate()}
          onClose={() => {
            add.reset();
            setOpen(false);
          }}
        >
          <ComboBox
            id="add-channel-stores-channel"
            titleText={t('channelStores.addAll.channel')}
            items={channelOptions}
            itemToString={label}
            selectedItem={channelOptions.find((o) => o.value === channel) ?? null}
            onChange={({ selectedItem }) => setChannel(selectedItem?.value)}
          />
          <ComboBox
            id="add-channel-stores-brand"
            titleText={t('channelStores.addAll.brand')}
            helperText={t('channelStores.addAll.brandHelp')}
            items={brandOptions}
            itemToString={label}
            selectedItem={brandOptions.find((o) => o.value === brand) ?? null}
            onChange={({ selectedItem }) => setBrand(selectedItem?.value)}
          />
          <RadioButtonGroup
            legendText={t('channelStores.addAll.status')}
            name="add-channel-stores-status"
            valueSelected={status}
            onChange={(value) => setStatus(value as 'closed' | 'open')}
            orientation="vertical"
          >
            <RadioButton id="add-channel-stores-closed" value="closed" labelText={t('channelStores.addAll.closed')} />
            <RadioButton id="add-channel-stores-open" value="open" labelText={t('channelStores.addAll.open')} />
          </RadioButtonGroup>
        </FormModal>
      )}
    </>
  );
}
